import type { CSSProperties } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, TENANT_TOKEN_KEY } from '../../api/client';
import { channelLabel } from './conversationChannelMeta';

type IntentRow = {
  id: string;
  label: string;
  count: number;
  share: number;
};

type MessageAnalyticsData = {
  period: { days: number; sinceIso: string; untilIso: string };
  virtualAgentId: string | null;
  truncatedVisitorSample: boolean;
  visitorMessagesTotal: number;
  visitorMessagesAnalyzed: number;
  agentMessagesTotal: number;
  conversationsTouched: number;
  topVisitorQuestions: Array<{ text: string; count: number; share: number }>;
  intentDistribution: IntentRow[];
  agentSourceDistribution: Array<{ source: string | null; count: number; share: number }>;
  semanticShortcutShare: number | null;
  modelFallbackShare: number | null;
  operatorFeedback: { rated: number; positive: number; negative: number };
  messagesByChannel: Array<{ channel: string; visitorCount: number }>;
  messagesByHourUtc: Array<{ hour: number; visitorCount: number }>;
  visitorMessageLength: { avgChars: number; medianChars: number };
  recommendations: Array<{ title: string; detail: string; priority: 'alta' | 'media' | 'baja' }>;
};

type AgentRow = { id: string; name: string; isActive: boolean };

/** Máximo de filas por tabla o lista en esta pantalla (alineado con el API de analíticas). */
const ANALYTICS_TABLE_ROWS = 10;

const card: CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  padding: 18,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

const priorityColor: Record<string, string> = {
  alta: '#b91c1c',
  media: '#b45309',
  baja: '#475569',
};

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

export function MessageAnalyticsPage() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY)!;
  const [days, setDays] = useState(30);
  const [agentId, setAgentId] = useState<string>('');
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [data, setData] = useState<MessageAnalyticsData | null>(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const loadAgents = useCallback(async () => {
    try {
      const rows = await apiGet<AgentRow[]>('/api/console/agents', token);
      setAgents(rows.filter((a) => a.isActive).length ? rows.filter((a) => a.isActive) : rows);
    } catch {
      setAgents([]);
    }
  }, [token]);

  const run = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('days', String(days));
      if (agentId.trim()) qs.set('virtualAgentId', agentId.trim());
      const d = await apiGet<MessageAnalyticsData>(
        `/api/console/conversations/stats/messages?${qs.toString()}`,
        token
      );
      setData(d);
    } catch (e) {
      setErr((e as Error).message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, days, agentId]);

  useEffect(() => {
    void loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    void run();
  }, [run]);

  const peakHourLabel = (() => {
    if (!data?.messagesByHourUtc.length) return null;
    const ph = data.messagesByHourUtc.reduce((a, b) => (b.visitorCount > a.visitorCount ? b : a));
    return { label: `${ph.hour}:00-${ph.hour}:59 UTC`, count: ph.visitorCount };
  })();

  const showLoadingMessage = data === null && loading === false && err === '';

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 24px 40px' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: '#0f172a' }}>
            Análisis de mensajes
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#64748b' }}>
            Explorá todos los mensajes, filtrados por fecha, canal y tipo.
          </p>
        </div>
      </header>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'flex-end',
          marginBottom: 22,
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#475569' }}>
          Período (días)
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', minWidth: 120 }}
          >
            {[7, 14, 30, 60, 90, 180, 365].map((d) => (
              <option key={d} value={d}>
                {d} días
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#475569' }}>
          Agente (opcional)
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', minWidth: 220 }}
          >
            <option value="">Todos los agentes</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => void run()}
          disabled={loading}
          style={{
            padding: '10px 18px',
            borderRadius: 8,
            border: 'none',
            background: loading ? '#94a3b8' : '#0f172a',
            color: '#fff',
            fontWeight: 700,
            cursor: loading ? 'default' : 'pointer',
            fontSize: 14,
          }}
        >
          {loading ? 'Analizando…' : 'Actualizar'}
        </button>
        <Link to="/app/learnings" style={{ fontSize: 13, color: '#2563eb', fontWeight: 600 }}>
          Ir a Aprendizajes →
        </Link>
      </div>

      {err ? (
        <p style={{ color: '#b91c1c', marginBottom: 16 }}>
          {err}{' '}
          <button type="button" onClick={() => void run()} style={{ color: '#2563eb', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
            Reintentar
          </button>
        </p>
      ) : null}

      {data?.truncatedVisitorSample ? (
        <p style={{ fontSize: 13, color: '#b45309', marginBottom: 16, padding: 12, background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
          Se analizaron hasta {data.visitorMessagesAnalyzed.toLocaleString('es-UY')} mensajes del visitante (muestra
          reciente). El total en el período es {data.visitorMessagesTotal.toLocaleString('es-UY')}: las FAQ e intenciones
          reflejan esa muestra.
        </p>
      ) : null}

      {data ? (
        <>
          <section
            style={{
              display: 'grid',
              gap: 14,
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              marginBottom: 20,
            }}
          >
            <div style={{ ...card, textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Visitante</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{data.visitorMessagesTotal}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>mensajes en período</div>
            </div>
            <div style={{ ...card, textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Agente</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{data.agentMessagesTotal}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>respuestas en período</div>
            </div>
            <div style={{ ...card, textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Conversaciones</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{data.conversationsTouched}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>con al menos un mensaje visitante</div>
            </div>
            <div style={{ ...card, textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Atajos semánticos</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>
                {data.semanticShortcutShare === null ? '—' : pct(data.semanticShortcutShare)}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>de respuestas del agente</div>
            </div>
            <div style={{ ...card, textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Fallback modelo</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>
                {data.modelFallbackShare === null ? '—' : pct(data.modelFallbackShare)}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>respuestas con modelo de reserva</div>
            </div>
          </section>

          <div style={{ ...card, marginBottom: 20 }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Recomendaciones</h2>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {data.recommendations.slice(0, ANALYTICS_TABLE_ROWS).map((r, i) => (
                <li key={i} style={{ fontSize: 14, color: '#334155', lineHeight: 1.5 }}>
                  <span style={{ fontWeight: 800, color: priorityColor[r.priority] ?? '#334155' }}>[{r.priority}]</span>{' '}
                  <strong>{r.title}</strong> — {r.detail}
                </li>
              ))}
            </ul>
          </div>

          <div
            style={{
              display: 'grid',
              gap: 18,
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              marginBottom: 20,
            }}
          >
            <div style={card}>
              <h2 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                Preguntas más frecuentes (texto normalizado)
              </h2>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b' }}>
                Agrupación por texto similar (sin IA). Período: {new Date(data.period.sinceIso).toLocaleDateString('es-UY')}{' '}
                — {new Date(data.period.untilIso).toLocaleDateString('es-UY')}.
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>
                    <th style={{ padding: '6px 0' }}>#</th>
                    <th style={{ padding: '6px 0' }}>Ejemplo / pregunta</th>
                    <th style={{ padding: '6px 0', textAlign: 'right' }}>Veces</th>
                    <th style={{ padding: '6px 0', textAlign: 'right' }}>% muestra</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topVisitorQuestions.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: '12px 0', color: '#94a3b8' }}>
                        Sin mensajes del visitante en el período.
                      </td>
                    </tr>
                  ) : (
                    data.topVisitorQuestions.slice(0, ANALYTICS_TABLE_ROWS).map((row, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 6px 8px 0', color: '#94a3b8', verticalAlign: 'top' }}>{idx + 1}</td>
                        <td style={{ padding: '8px 0', color: '#1e293b', verticalAlign: 'top', wordBreak: 'break-word' }}>
                          {row.text}
                        </td>
                        <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 700, verticalAlign: 'top' }}>
                          {row.count}
                        </td>
                        <td style={{ padding: '8px 0', textAlign: 'right', color: '#64748b', verticalAlign: 'top' }}>
                          {pct(row.share)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div style={card}>
              <h2 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                Intenciones estimadas (visitante)
              </h2>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b' }}>
                Orden de reglas fijas (saludo, humano, fotos, precios…). Útil para ver de qué habla la gente aunque no
                tengas clasificación en BD.
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>
                    <th style={{ padding: '6px 0' }}>Intención</th>
                    <th style={{ padding: '6px 0', textAlign: 'right' }}>Mensajes</th>
                    <th style={{ padding: '6px 0', textAlign: 'right' }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.intentDistribution.slice(0, ANALYTICS_TABLE_ROWS).map((row) => (
                    <tr key={row.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 0', color: '#1e293b' }}>{row.label}</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 700 }}>{row.count}</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', color: '#64748b' }}>{pct(row.share)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gap: 18,
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              marginBottom: 20,
            }}
          >
            <div style={card}>
              <h2 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Origen de respuesta (agente)</h2>
              <p style={{ margin: '0 0 8px', fontSize: 12, color: '#64748b' }}>Campo agentSource en cada mensaje del agente.</p>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#334155' }}>
                {data.agentSourceDistribution.slice(0, ANALYTICS_TABLE_ROWS).map((r) => (
                  <li key={String(r.source)} style={{ marginBottom: 6 }}>
                    <strong>{r.source ?? '(vacío)'}</strong> — {r.count} ({pct(r.share)})
                  </li>
                ))}
              </ul>
            </div>

            <div style={card}>
              <h2 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Canales (visitante)</h2>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#334155' }}>
                {data.messagesByChannel.slice(0, ANALYTICS_TABLE_ROWS).map((r) => (
                  <li key={r.channel} style={{ marginBottom: 6 }}>
                    {channelLabel(r.channel)} — {r.visitorCount}
                  </li>
                ))}
              </ul>
            </div>

            <div style={card}>
              <h2 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Horario pico (UTC)</h2>
              <p style={{ margin: '0 0 8px', fontSize: 13, color: '#334155' }}>
                Hora con más mensajes del visitante (en la muestra analizada):{' '}
                <strong>
                  {peakHourLabel ? `${peakHourLabel.label} (${peakHourLabel.count} mensajes)` : '—'}
                </strong>
              </p>
              <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
                Longitud media del mensaje visitante: {data.visitorMessageLength.avgChars} caracteres · mediana:{' '}
                {data.visitorMessageLength.medianChars}.
              </p>
            </div>

            <div style={card}>
              <h2 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Feedback en consola</h2>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                Valoraciones sobre respuestas del agente: <strong>{data.operatorFeedback.rated}</strong> en total · útiles:{' '}
                <strong style={{ color: '#15803d' }}>{data.operatorFeedback.positive}</strong> · a mejorar:{' '}
                <strong style={{ color: '#b91c1c' }}>{data.operatorFeedback.negative}</strong>
              </p>
            </div>
          </div>
        </>
      ) : null}

      {showLoadingMessage ? <p style={{ color: '#64748b' }}>Cargando...</p> : null}
    </div>
  );
}
